/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { lazy } from 'react';

// login page
export const LoginPage = lazy(() =>
  import('@trebox-foundation/account-ui-adapter').then(res => ({
    default: res.LoginPage,
  })),
);

// OIDC callback page · receives the token JSON via query params after the
// backend completes the IdP code exchange.
export const OAuthCallbackPage = lazy(() =>
  import('@trebox-foundation/account-ui-adapter').then(res => ({
    default: res.OAuthCallbackPage,
  })),
);

// documentation page
export const Redirect = lazy(() => import('../pages/redirect'));

// chat page (trebox-native · design 1:1 shell + chat-area middle)
export const ChatPage = lazy(() => import('../pages/chat'));

// resource module · resource-tree (folders + page nodes) + Word/Excel online
// editing (OnlyOffice). Office documents are page nodes in the resource tree.
export const ResourcesPage = lazy(() => import('../pages/resources'));

// device-fleet list page · proxies xiaozhi.me agents via backend.
export const DeviceFleetPage = lazy(() => import('../pages/devicefleet'));

// persona library · operator (platform-admin) PersonaPreset CRUD.
export const PersonasPage = lazy(() => import('../pages/personas'));

// A13 model-configuration console · operator model-instance CRUD per family
// (catalog + connection form rendered from GET /api/model/providers).
export const ModelSettingsPage = lazy(() => import('../pages/settings/models'));

// firmware distribution · operator (releases / rollout rules / upgrade history).
export const FirmwarePage = lazy(() => import('../pages/devicefleet/firmware'));

// Operator flash/repair page (route /devicefleet/register) · connected-device
// registration: reflash → app-mode serial export/sign → register-connected.
export const ConnectedRegisterPage = lazy(
  () => import('../pages/devicefleet/register'),
);

// Companion Workspace · device-centric detail (route /devicefleet/:deviceId) —
// conversations / memory / persona / compliance tabs for one device + owner.
export const CompanionWorkspacePage = lazy(
  () => import('../pages/devicefleet/companion'),
);

// public no-login web-flash page (/open/flash) · esptool-js Web Serial.
export const FlashPage = lazy(() => import('../pages/open/flash'));

// trebox-native persistent shell · LeftRail + Outlet for /chat /resources /tools …
export const TreboxShellLayout = lazy(
  () => import('../layouts/trebox-shell-layout'),
);

// Workspace Sidebar Component
export const spaceSubMenu = lazy(() =>
  import('@trebox-foundation/space-ui-adapter').then(exps => ({
    default: exps.WorkspaceSubMenu,
  })),
);

// Workspace Layout Component
export const SpaceLayout = lazy(() =>
  import('@trebox-foundation/space-ui-adapter').then(exps => ({
    default: exps.SpaceLayout,
  })),
);

// A specific workspace layout component
export const SpaceIdLayout = lazy(() =>
  import('@trebox-foundation/space-ui-base').then(exps => ({
    default: exps.SpaceIdLayout,
  })),
);

// project development page
export const Develop = lazy(() => import('../pages/develop'));

// resource library page
export const Library = lazy(() => import('../pages/library'));

// Agent IDE Layout Component
export const AgentIDELayout = lazy(
  () => import('@trebox-agent-ide/layout-adapter'),
);

// Agent IDE page
export const AgentIDE = lazy(() =>
  import('@trebox-agent-ide/entry-adapter').then(res => ({
    default: res.BotEditor,
  })),
);

// Agent IDE Release Page
export const AgentPublishPage = lazy(() =>
  import('@trebox-agent-ide/agent-publish').then(exps => ({
    default: exps.AgentPublishPage,
  })),
);

// Project IDE Page
export const ProjectIDE = lazy(() =>
  import('@trebox-project-ide/main').then(exps => ({
    default: exps.IDELayout,
  })),
);

// Project IDE Release Page
export const ProjectIDEPublish = lazy(() =>
  import('@trebox-studio/project-publish').then(exps => ({
    default: exps.ProjectPublish,
  })),
);

// Knowledge Base Preview Page
export const KnowledgePreview = lazy(() =>
  import('@trebox-studio/workspace-base/knowledge-preview').then(exps => ({
    default: exps.KnowledgePreviewPage,
  })),
);

// Knowledge base upload page
export const KnowledgeUpload = lazy(() =>
  import('@trebox-studio/workspace-base/knowledge-upload').then(exps => ({
    default: exps.KnowledgeUploadPage,
  })),
);

// database resource page
export const DatabaseDetail = lazy(() =>
  import('@trebox-studio/workspace-base').then(exps => ({
    default: exps.DatabaseDetailPage,
  })),
);

// workflow page
export const WorkflowPage = lazy(() =>
  import('@trebox-workflow/playground-adapter').then(res => ({
    default: res.WorkflowPage,
  })),
);

// search page
export const SearchPage = lazy(() =>
  import('@trebox-community/explore').then(exps => ({
    default: exps.SearchPage,
  })),
);

// plugin resource page layout component
export const PluginLayout = lazy(() => import('../pages/plugin/layout'));

// plugin resource page
export const PluginPage = lazy(() => import('../pages/plugin/page'));

// plugin tool page
export const PluginToolPage = lazy(() => import('../pages/plugin/tool/page'));

// Explore the experience page secondary navigation component
export const exploreSubMenu = lazy(() =>
  import('@trebox-community/explore').then(exps => ({
    default: exps.ExploreSubMenu,
  })),
);

// template page
export const ExploreTemplatePage = lazy(() =>
  import('@trebox-community/explore').then(exps => ({
    default: exps.TemplatePage,
  })),
);

// plugin store page
export const ExplorePluginPage = lazy(() =>
  import('@trebox-community/explore').then(exps => ({
    default: exps.PluginPage,
  })),
);

// OAuth consent confirm page
export const OAuthConsentConfirmPage = lazy(() =>
  import('@trebox-studio/open-auth').then(exps => ({
    default: exps.ConsentConfirmPage,
  })),
);
